// Hook standalone — gerencia o CRUD da tabela cost_of_goods_entries.
// Não toca em products/sales. Auto-cria a linha do mês corrente ao montar.

import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { CostOfGoodsEntry } from '../types'

export interface CostOfGoodsInput {
  period_year: number
  period_month: number
  cost_matriz?: number
  cost_filial?: number
  cost_filial_return?: number
  revenue_gross?: number
  target_pct?: number
  notes?: string | null
}

export function useCostOfGoods() {
  const [entries, setEntries] = useState<CostOfGoodsEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const ensuredRef = useRef(false) // garante que ensureCurrentMonth roda apenas uma vez

  const fetchEntries = useCallback(async (): Promise<CostOfGoodsEntry[]> => {
    const { data, error: err } = await supabase
      .from('cost_of_goods_entries')
      .select('*')
      .order('period_year', { ascending: true })
      .order('period_month', { ascending: true })
    if (err) {
      setError(err.message)
      return []
    }
    setError(null)
    const list = (data ?? []) as CostOfGoodsEntry[]
    setEntries(list)
    return list
  }, [])

  // Auto-cria a entry do mês corrente se não existir (alimentação sequencial).
  const ensureCurrentMonth = useCallback(async (current: CostOfGoodsEntry[]) => {
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth() + 1
    const exists = current.some(e => e.period_year === year && e.period_month === month)
    if (exists) return
    const { error: err } = await supabase
      .from('cost_of_goods_entries')
      .insert({ period_year: year, period_month: month })
    if (!err) await fetchEntries()
  }, [fetchEntries])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const list = await fetchEntries()
      if (cancelled) return
      if (!ensuredRef.current) {
        ensuredRef.current = true
        await ensureCurrentMonth(list)
      }
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [fetchEntries, ensureCurrentMonth])

  // INSERT ou UPDATE (upsert por unique key company_id+year+month)
  const upsertEntry = useCallback(async (input: CostOfGoodsInput): Promise<string | null> => {
    const { error: err } = await supabase
      .from('cost_of_goods_entries')
      .upsert(input, { onConflict: 'company_id,period_year,period_month' })
    if (err) return err.message
    await fetchEntries()
    return null
  }, [fetchEntries])

  // Update direto por id (preserva company_id e period_*)
  const updateById = useCallback(async (id: string, patch: Partial<CostOfGoodsInput>): Promise<string | null> => {
    const { error: err } = await supabase
      .from('cost_of_goods_entries')
      .update(patch)
      .eq('id', id)
    if (err) return err.message
    await fetchEntries()
    return null
  }, [fetchEntries])

  const deleteEntry = useCallback(async (id: string): Promise<string | null> => {
    const { error: err } = await supabase
      .from('cost_of_goods_entries')
      .delete()
      .eq('id', id)
    if (err) return err.message
    await fetchEntries()
    return null
  }, [fetchEntries])

  // Dispara o sync das notas de entrada do Tiny (edge function). Faz o loop de
  // continuação (a function sai perto do timeout e devolve nextOffset).
  const syncNotas = useCallback(async (): Promise<string | null> => {
    let offset = 0
    for (let guard = 0; guard < 40; guard++) {
      const { data, error: err } = await supabase.functions.invoke('tiny-sync-notas-v3', {
        body: { offset },
      })
      if (err) return err.message
      const res = data as { ok?: boolean; done?: boolean; nextOffset?: number; error?: string }
      if (!res?.ok) return res?.error ?? 'Falha ao sincronizar notas'
      if (res.done) break
      offset = Number(res.nextOffset ?? offset)
    }
    await fetchEntries()
    return null
  }, [fetchEntries])

  // Notas de entrada (com parcelas) de um mês — para o drill-down da aba.
  const fetchMonthInvoices = useCallback(async (year: number, month: number): Promise<PurchaseInvoiceRow[]> => {
    const start = `${year}-${String(month).padStart(2, '0')}-01`
    const nextY = month === 12 ? year + 1 : year
    const nextM = month === 12 ? 1 : month + 1
    const end = `${nextY}-${String(nextM).padStart(2, '0')}-01`
    const { data, error: err } = await supabase
      .from('purchase_invoices')
      .select('id, numero, serie, emitente_nome, emitente_cnpj, cfop, data_emissao, situacao, valor_total, condicao_pagamento, purchase_invoice_installments(numero, dias, due_date, valor, id_meio_pagamento, observacoes)')
      .gte('data_emissao', start)
      .lt('data_emissao', end)
      .order('data_emissao', { ascending: true })
    if (err) return []
    return (data ?? []).map((r) => {
      const row = r as Record<string, unknown>
      const parcelas = (row.purchase_invoice_installments as InstallmentRow[] | undefined) ?? []
      return {
        id: String(row.id),
        numero: (row.numero as string) ?? null,
        serie: (row.serie as string) ?? null,
        emitente_nome: (row.emitente_nome as string) ?? null,
        emitente_cnpj: (row.emitente_cnpj as string) ?? null,
        cfop: (row.cfop as string) ?? null,
        data_emissao: (row.data_emissao as string) ?? null,
        situacao: (row.situacao as string) ?? null,
        valor_total: Number(row.valor_total) || 0,
        condicao_pagamento: (row.condicao_pagamento as string) ?? null,
        installments: parcelas
          .map(p => ({
            numero: p.numero ?? null,
            dias: p.dias ?? null,
            due_date: p.due_date ?? null,
            valor: Number(p.valor) || 0,
            id_meio_pagamento: p.id_meio_pagamento ?? null,
            observacoes: p.observacoes ?? null,
          }))
          .sort((a, b) => (a.numero ?? 0) - (b.numero ?? 0)),
      }
    })
  }, [])

  // Boletos (parcelas) das notas de entrada da EMPRESA ATIVA, com fornecedor/NF,
  // ordenados por vencimento. Consulta direta na tabela — a RLS já restringe à
  // empresa ativa; sem junção por grupo/CNPJ (cada empresa vê só o seu).
  const fetchAllBoletos = useCallback(async (): Promise<BoletoRow[]> => {
    const { data, error: err } = await supabase
      .from('purchase_invoice_installments')
      .select('id, invoice_id, company_id, numero, due_date, valor, id_meio_pagamento, observacoes, purchase_invoices!inner(numero, serie, emitente_nome, data_emissao, situacao)')
      .order('due_date', { ascending: true })
    if (err) return []
    return (data ?? [])
      .map((r) => {
        const row = r as Record<string, unknown>
        const inv = (row.purchase_invoices ?? {}) as Record<string, unknown>
        return {
          id: String(row.id),
          invoice_id: String(row.invoice_id),
          company_id: (row.company_id as string) ?? null,
          company_name: null,
          numero: (row.numero as number) ?? null,
          due_date: (row.due_date as string) ?? null,
          valor: Number(row.valor) || 0,
          id_meio_pagamento: (row.id_meio_pagamento as string) ?? null,
          observacoes: (row.observacoes as string) ?? null,
          nf_numero: (inv.numero as string) ?? null,
          nf_serie: (inv.serie as string) ?? null,
          emitente_nome: (inv.emitente_nome as string) ?? null,
          data_emissao: (inv.data_emissao as string) ?? null,
          situacao: (inv.situacao as string) ?? null,
        }
      })
      // Só notas autorizadas/faturadas (situação 8) — os dados de pagamento
      // (data/valor/parcelas) só existem quando a nota está faturada.
      .filter(b => b.situacao === '8')
  }, [])

  // Cria a próxima linha sequencial a partir da última.
  const addNextMonth = useCallback(async (): Promise<string | null> => {
    let year: number
    let month: number
    if (entries.length === 0) {
      const now = new Date()
      year = now.getFullYear()
      month = now.getMonth() + 1
    } else {
      const last = entries[entries.length - 1]
      year = last.period_year
      month = last.period_month + 1
      if (month > 12) { month = 1; year += 1 }
    }
    return upsertEntry({ period_year: year, period_month: month })
  }, [entries, upsertEntry])

  return {
    entries,
    loading,
    error,
    upsertEntry,
    updateById,
    deleteEntry,
    addNextMonth,
    syncNotas,
    fetchMonthInvoices,
    fetchAllBoletos,
    refetch: fetchEntries,
  }
}

// ── Drill-down: notas de entrada + parcelas ──────────────────────

export interface InstallmentRow {
  numero: number | null
  dias: number | null
  due_date: string | null
  valor: number
  id_meio_pagamento: string | null
  observacoes: string | null
}

// Boleto (parcela) achatado com dados da nota-mãe — para "Boletos a Pagar".
export interface BoletoRow {
  id: string
  invoice_id: string
  company_id: string | null
  company_name: string | null
  numero: number | null
  due_date: string | null
  valor: number
  id_meio_pagamento: string | null
  observacoes: string | null
  nf_numero: string | null
  nf_serie: string | null
  emitente_nome: string | null
  data_emissao: string | null
  situacao: string | null
}

export interface PurchaseInvoiceRow {
  id: string
  numero: string | null
  serie: string | null
  emitente_nome: string | null
  emitente_cnpj: string | null
  cfop: string | null
  data_emissao: string | null
  situacao: string | null
  valor_total: number
  condicao_pagamento: string | null
  installments: InstallmentRow[]
}

// ── Previsão de contas a pagar (a partir dos boletos) ────────────

/** Hoje no fuso local, 'YYYY-MM-DD' (mesmo formato do due_date). */
export function todayLocalISO(now: Date = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Aritmética de mês em 'YYYY-MM' (n pode ser negativo). */
export function addMonthsYM(ym: string, n: number): string {
  const [y, m] = ym.split('-').map(Number)
  const total = y * 12 + (m - 1) + n
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`
}

export interface DueMonthBucket {
  ym: string        // 'YYYY-MM' do vencimento
  label: string     // 'jul/26'
  total: number     // Σ valor das parcelas que vencem no mês
  count: number     // nº de boletos
  bucket: 'passado' | 'atual' | 'futuro'
}

/** Soma os boletos por mês de VENCIMENTO → previsão mensal de contas a pagar.
 *  Janela: de `pastWindow` meses atrás (default 3) até o mês futuro mais
 *  distante com dados. Boletos sem vencimento são ignorados. */
export function forecastByDueMonth(
  boletos: ReadonlyArray<BoletoRow>,
  todayISO: string,
  pastWindow = 3,
): DueMonthBucket[] {
  const currentYM = todayISO.slice(0, 7)
  const cutoff = addMonthsYM(currentYM, -pastWindow)
  const acc = new Map<string, { total: number; count: number }>()
  for (const b of boletos) {
    if (!b.due_date) continue
    const ym = b.due_date.slice(0, 7)
    if (ym < cutoff) continue
    const cur = acc.get(ym) ?? { total: 0, count: 0 }
    cur.total += b.valor
    cur.count += 1
    acc.set(ym, cur)
  }
  return [...acc.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([ym, v]) => {
      const [y, m] = ym.split('-').map(Number)
      return {
        ym,
        label: monthLabel(y, m),
        total: v.total,
        count: v.count,
        bucket: (ym < currentYM ? 'passado' : ym === currentYM ? 'atual' : 'futuro') as DueMonthBucket['bucket'],
      }
    })
}

export interface PayablesTotals {
  aberto: number          // vence hoje ou no futuro (a pagar)
  vencido: number         // já venceu
  proximo: string | null  // vencimento da próxima parcela futura ('YYYY-MM-DD')
}

/** Totais de contas a pagar relativos a `todayISO`. */
export function payablesTotals(boletos: ReadonlyArray<BoletoRow>, todayISO: string): PayablesTotals {
  let aberto = 0, vencido = 0
  let proximo: string | null = null
  for (const b of boletos) {
    if (!b.due_date) continue
    if (b.due_date < todayISO) {
      vencido += b.valor
    } else {
      aberto += b.valor
      if (proximo === null || b.due_date < proximo) proximo = b.due_date
    }
  }
  return { aberto, vencido, proximo }
}

// ── Cálculos derivados (puro, sem side-effects) ──────────────────

export interface ComputedRow {
  costReal: number
  costPct: number | null
  targetValue: number
  status: 'ok' | 'warning' | 'danger' | 'empty'
}

export function computeRow(e: CostOfGoodsEntry): ComputedRow {
  const costReal = (e.cost_matriz ?? 0) - (e.cost_filial ?? 0) + (e.cost_filial_return ?? 0)
  const target = e.target_pct ?? 55
  const targetValue = (e.revenue_gross ?? 0) * (target / 100)
  const costPct = e.revenue_gross > 0 ? (costReal / e.revenue_gross) * 100 : null
  let status: ComputedRow['status']
  if (costPct == null) status = 'empty'
  else if (costPct <= target) status = 'ok'
  else if (costPct <= target + 5) status = 'warning'
  else status = 'danger'
  return { costReal, costPct, targetValue, status }
}

export function monthLabel(year: number, month: number): string {
  const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
  return `${months[month - 1]}/${String(year).slice(-2)}`
}

/** Custo real (matriz, líquido de filial) por mês → Map `YYYY-MM → costReal`.
 *  Base do cap mensal de compras (o usuário gerencia o teto pela Custo de Mercadoria). */
export function costRealByMonth(entries: ReadonlyArray<CostOfGoodsEntry>): Map<string, number> {
  const m = new Map<string, number>()
  for (const e of entries) {
    const key = `${e.period_year}-${String(e.period_month).padStart(2, '0')}`
    m.set(key, computeRow(e).costReal)
  }
  return m
}
